/**
 * <gff-gallery> — Good Family Farms PDP image gallery.
 *
 * Click a thumb to swap the main image. Keyboard: Enter/Space to select,
 * ArrowLeft / ArrowRight to navigate. Caption text + counter update in sync.
 * No-JS fallback: first image renders as the main, thumbs are visible but
 * not interactive (their inline background-image previews the image).
 *
 * Markup contract (Liquid emits this):
 *   <gff-gallery>
 *     <div class="gff-gal__main" data-gff-gal-main style="background-image: ...">
 *       <div class="gff-gal__caption">
 *         <small data-gff-gal-caption-counter>01 / 04</small>
 *         <span data-gff-gal-caption-text>Hand-wrapped at the farm</span>
 *       </div>
 *     </div>
 *     <div class="gff-gal__thumbs" role="tablist">
 *       <button data-gff-gal-thumb data-src="...full" data-alt="..." class="is-active">…</button>
 *       …
 *     </div>
 *   </gff-gallery>
 */
class GffGallery extends HTMLElement {
  connectedCallback() {
    this.thumbs = Array.from(this.querySelectorAll('[data-gff-gal-thumb]'));
    this.main = this.querySelector('[data-gff-gal-main]');
    this.caption = this.querySelector('[data-gff-gal-caption-text]');
    this.counter = this.querySelector('[data-gff-gal-caption-counter]');

    if (this.thumbs.length === 0 || !this.main) return;

    for (const [index, thumb] of this.thumbs.entries()) {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        this.show(index);
      });
      thumb.addEventListener('keydown', (e) => {
        switch (e.key) {
          case 'Enter':
          case ' ':
            e.preventDefault();
            this.show(index);
            break;
          case 'ArrowRight':
            e.preventDefault();
            this.show((index + 1) % this.thumbs.length);
            this.thumbs[(index + 1) % this.thumbs.length].focus();
            break;
          case 'ArrowLeft':
            e.preventDefault();
            this.show((index - 1 + this.thumbs.length) % this.thumbs.length);
            this.thumbs[(index - 1 + this.thumbs.length) % this.thumbs.length].focus();
            break;
        }
      });
    }
  }

  show(i) {
    const thumb = this.thumbs[i];
    if (!thumb) return;

    const src = thumb.dataset.src;
    const focus = thumb.dataset.focus || 'center center';
    const alt = thumb.dataset.alt || '';

    if (src) {
      this.main.style.backgroundImage = `url(${src})`;
      this.main.style.backgroundPosition = focus;
    }
    if (this.caption) this.caption.textContent = alt;
    if (this.counter) {
      const pad = (n) => String(n).padStart(2, '0');
      this.counter.textContent = `${pad(i + 1)} / ${pad(this.thumbs.length)}`;
    }

    for (const [j, t] of this.thumbs.entries()) {
      const active = i === j;
      t.classList.toggle('is-active', active);
      t.setAttribute('aria-current', active ? 'true' : 'false');
    }
  }
}

if (!customElements.get('gff-gallery')) {
  customElements.define('gff-gallery', GffGallery);
}

/**
 * <gff-variants> — Variant picker for the GFF PDP buy box.
 *
 * Click a [data-gff-variant] radio/button to update the parent form's
 * hidden input[name="id"] and the browser URL's ?variant param. Compatible
 * with Horizon's <product-form-component> which submits the form via ajax.
 *
 * Markup contract:
 *   <gff-variants>
 *     <label class="gff-variant"><input type="radio" name="id" value="123" checked>…</label>
 *     <label class="gff-variant"><input type="radio" name="id" value="456">…</label>
 *   </gff-variants>
 */
class GffVariants extends HTMLElement {
  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('input[type="radio"]'));
    for (const input of this.inputs) {
      input.addEventListener('change', () => {
        if (!input.checked) return;
        const variantId = input.value;
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url.toString());
        if (input.dataset.price) {
          for (const el of document.querySelectorAll('.gff-buy__price, .gff-sticky__price')) {
            el.textContent = input.dataset.price;
          }
        }
        this.dispatchEvent(new CustomEvent('gff:variant-change', {
          bubbles: true,
          detail: { variantId },
        }));
      });
    }
  }
}

if (!customElements.get('gff-variants')) {
  customElements.define('gff-variants', GffVariants);
}

/**
 * <gff-selling-plans> — Selling-plan radios on the GFF PDP form.
 *
 * Updates the buy-box price from per-variant allocations and the add-button
 * label. Writes `name="selling_plan"` so Horizon product-form ajax add
 * includes the selected plan id (disabled inputs are omitted).
 */
class GffSellingPlans extends HTMLElement {
  #onVariantChange = null;

  connectedCallback() {
    this.form = this.closest('form');
    this.buy = this.closest('.gff-buy');
    this.modeInputs = Array.from(this.querySelectorAll('[data-gff-mode]'));
    this.inputs = Array.from(this.querySelectorAll('input[name="selling_plan"]'));
    this.priceMap = this.#readPriceMap();
    this.variantId = this.dataset.variantId || this.#currentVariantId();

    if (this.modeInputs.length) {
      for (const input of this.modeInputs) {
        input.addEventListener('change', () => this.#onModeChange());
      }
      this.#onModeChange();
    } else {
      this.classList.add('is-subscribe');
      for (const input of this.inputs) input.disabled = false;
    }

    for (const input of this.inputs) {
      input.addEventListener('change', () => this.#applySelected());
    }

    this.#onVariantChange = (event) => {
      if (event.detail?.variantId) this.variantId = String(event.detail.variantId);
      this.#refreshOptionPrices();
      this.#applySelected();
    };
    this.buy?.addEventListener('gff:variant-change', this.#onVariantChange);

    this.#refreshOptionPrices();
    this.#applySelected();
  }

  disconnectedCallback() {
    this.buy?.removeEventListener('gff:variant-change', this.#onVariantChange);
  }

  #isSubscribeMode() {
    if (!this.modeInputs.length) return true;
    return this.modeInputs.some((input) => input.checked && input.value === 'subscribe');
  }

  #onModeChange() {
    const subscribe = this.#isSubscribeMode();
    this.classList.toggle('is-subscribe', subscribe);
    for (const input of this.inputs) {
      input.disabled = !subscribe;
    }
    this.#applySelected();
  }

  #currentVariantId() {
    const checked = this.form?.querySelector('input[name="id"]:checked, input[name="id"][type="hidden"]');
    return checked ? String(checked.value) : '';
  }

  #readPriceMap() {
    const node = this.querySelector('[data-gff-plan-prices]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent || '{}');
    } catch {
      return {};
    }
  }

  #formatCents(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n)) return '';
    const dollars = n / 100;
    if (Math.abs(dollars - Math.round(dollars)) < 1e-9) return `$${Math.round(dollars)}`;
    return `$${dollars.toFixed(2)}`;
  }

  #oneTimeCents() {
    const variantId = this.variantId || this.#currentVariantId();
    const fromMap = this.priceMap[variantId]?.[''];
    if (fromMap !== undefined) return fromMap;
    const raw = this.dataset.oneTimePriceCents;
    if (raw === undefined || raw === '') return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }

  #refreshOptionPrices() {
    const variantId = this.variantId || this.#currentVariantId();
    const variantPrices = this.priceMap[variantId] || {};
    for (const input of this.inputs) {
      const cents = variantPrices[input.value];
      if (cents === undefined) continue;
      input.dataset.price = this.#formatCents(cents);
    }
  }

  #subscribeButtonText(selected) {
    const base = this.dataset.subscribeLabel || 'Subscribe';
    if (!selected?.value) return base;
    const freq = selected.dataset.freqLabel?.trim();
    return freq ? `${base} · ${freq.toLowerCase()}` : base;
  }

  #setButtonLabels(labelText) {
    const label = this.buy?.querySelector('[data-gff-add-label]');
    const stickyLabel = document.querySelector('[data-gff-sticky-label]');
    const soldOut = this.form?.querySelector('.gff-buy__addbtn')?.disabled;
    if (soldOut) return;
    if (label) label.textContent = labelText;
    if (stickyLabel) stickyLabel.textContent = labelText;
  }

  #applySelected() {
    const subscribe = this.#isSubscribeMode();
    const selected = subscribe
      ? this.inputs.find((input) => input.checked && !input.disabled) || this.inputs.find((input) => !input.disabled)
      : null;

    const variantId = this.variantId || this.#currentVariantId();
    const variantPrices = this.priceMap[variantId] || {};
    let cents;
    if (selected?.value) {
      cents = variantPrices[selected.value];
    } else {
      cents = this.#oneTimeCents();
    }
    const formatted = cents !== undefined ? this.#formatCents(cents) : '';
    if (formatted) {
      for (const el of document.querySelectorAll('.gff-buy__price, .gff-sticky__price')) {
        el.textContent = formatted;
      }
    }

    let labelText = this.dataset.oneTimeLabel || 'Add to basket';
    if (subscribe && selected?.value) {
      labelText = this.#subscribeButtonText(selected);
    }
    this.#setButtonLabels(labelText);
  }
}

if (!customElements.get('gff-selling-plans')) {
  customElements.define('gff-selling-plans', GffSellingPlans);
}
